import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CalendarClock, ChevronRight, Pencil, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { ipdApi } from '@/api/ipd';
import type { IPDDischargeFollowUpItem } from '@/types/ipd';
import { useFrontDeskBasePath } from '@/hooks/useFrontDeskBasePath';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  formatDateTime,
  formatIstTimeHHmm,
  istDateAndTimeToIso,
  toIstDateInputValue,
} from '@/utils/dateUtils';
import { ensureDoctorPrefix } from '@/lib/doctorName';

function resolveFollowUpPayload(form: { followUpDate: string; followUpTime: string }) {
  const date = form.followUpDate.trim();
  const time = form.followUpTime.trim();
  if (date && !time) throw new Error('Follow-up time (IST) is required when date is set');
  if (!date && time) throw new Error('Follow-up date (IST) is required when time is set');
  if (!date || !time) throw new Error('Follow-up date and time (IST) are required');
  return istDateAndTimeToIso(date, time);
}

export default function IpdDischargeFollowUpsWidget() {
  const basePath = useFrontDeskBasePath();
  const queryClient = useQueryClient();
  const listPath = `${basePath}/ipd-discharge-follow-ups`;

  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [selected, setSelected] = useState<IPDDischargeFollowUpItem | null>(null);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  const [followUpReason, setFollowUpReason] = useState('');

  const { data: summary } = useQuery({
    queryKey: ['ipd-discharge-follow-up-summary'],
    queryFn: async () => {
      const res = await ipdApi.getDischargeFollowUpSummary();
      return res.data?.data ?? { todayCount: 0, upcomingCount: 0, overdueCount: 0 };
    },
    refetchInterval: 60000,
    staleTime: 15000,
  });

  const { data: todayItems = [], isLoading } = useQuery({
    queryKey: ['ipd-discharge-follow-ups', 'today'],
    queryFn: async () => {
      const res = await ipdApi.listDischargeFollowUps('today');
      return res.data?.data ?? [];
    },
    refetchInterval: 60000,
    staleTime: 15000,
  });

  const previewItems = useMemo(() => todayItems.slice(0, 6), [todayItems]);

  const rescheduleMut = useMutation({
    mutationFn: async (payload: {
      admissionId: string;
      followUpAt: string;
      followUpReason: string | null;
    }) => {
      await ipdApi.rescheduleDischargeFollowUp(payload.admissionId, {
        followUpAt: payload.followUpAt,
        followUpReason: payload.followUpReason,
      });
    },
    onSuccess: async () => {
      toast.success('Follow-up rescheduled');
      setRescheduleOpen(false);
      setSelected(null);
      await queryClient.invalidateQueries({ queryKey: ['ipd-discharge-follow-ups'] });
      await queryClient.invalidateQueries({ queryKey: ['ipd-discharge-follow-up-summary'] });
    },
    onError: (err: unknown) => {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || (err instanceof Error ? err.message : 'Failed to reschedule'));
    },
  });

  const openReschedule = (item: IPDDischargeFollowUpItem) => {
    setSelected(item);
    setFollowUpDate(item.followUpAt ? toIstDateInputValue(item.followUpAt) : '');
    setFollowUpTime(item.followUpAt ? formatIstTimeHHmm(item.followUpAt) : '');
    setFollowUpReason(item.followUpReason ?? '');
    setRescheduleOpen(true);
  };

  const handleSaveReschedule = () => {
    if (!selected) return;
    try {
      const followUpAt = resolveFollowUpPayload({ followUpDate, followUpTime });
      rescheduleMut.mutate({
        admissionId: selected.admissionId,
        followUpAt,
        followUpReason: followUpReason.trim() || null,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid follow-up date/time');
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarClock className="h-5 w-5" />
              IPD Discharge Follow-ups (IST)
            </CardTitle>
            <Link
              to={listPath}
              className="text-sm text-primary inline-flex items-center gap-1 hover:underline"
            >
              View all
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border bg-muted/40 p-3 text-center">
              <div className="text-2xl font-semibold">{summary?.todayCount ?? '—'}</div>
              <div className="text-xs text-muted-foreground mt-1">Today</div>
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 text-center">
              <div className="text-2xl font-semibold">{summary?.upcomingCount ?? '—'}</div>
              <div className="text-xs text-muted-foreground mt-1">Upcoming</div>
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 text-center">
              <div className="text-2xl font-semibold text-destructive">{summary?.overdueCount ?? '—'}</div>
              <div className="text-xs text-muted-foreground mt-1">Overdue</div>
            </div>
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Loading today&apos;s follow-ups…</p>
          ) : previewItems.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No IPD discharge follow-ups scheduled for today.
            </p>
          ) : (
            <ul className="divide-y rounded-md border">
              {previewItems.map((item) => {
                const patient = item.admission.queue.patient;
                const doctor = item.admission.assignedDoctor;
                return (
                  <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{patient.name}</div>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>{formatDateTime(item.followUpAt!)} IST</span>
                        <span>{ensureDoctorPrefix(doctor.name)}</span>
                        <a href={`tel:${patient.phone}`} className="inline-flex items-center gap-0.5 text-primary">
                          <Phone className="h-3 w-3" />
                          {patient.phone}
                        </a>
                      </div>
                      {item.followUpReason ? (
                        <div className="text-xs text-muted-foreground truncate mt-0.5">{item.followUpReason}</div>
                      ) : null}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => openReschedule(item)}>
                      <Pencil className="h-3.5 w-3.5 mr-1" />
                      Reschedule
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}

          {(summary?.todayCount ?? 0) > previewItems.length ? (
            <div className="text-center">
              <Link to={listPath} className="text-sm text-primary hover:underline">
                + {(summary?.todayCount ?? 0) - previewItems.length} more today
              </Link>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reschedule follow-up</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Patient:{' '}
                <span className="font-medium text-foreground">{selected.admission.queue.patient.name}</span>
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="widget-reschedule-date">Follow-up date (IST)</Label>
                  <Input
                    id="widget-reschedule-date"
                    type="date"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="widget-reschedule-time">Follow-up time (IST)</Label>
                  <Input
                    id="widget-reschedule-time"
                    type="time"
                    value={followUpTime}
                    onChange={(e) => setFollowUpTime(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="widget-reschedule-reason">Reason for follow-up</Label>
                <Textarea
                  id="widget-reschedule-reason"
                  value={followUpReason}
                  onChange={(e) => setFollowUpReason(e.target.value)}
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveReschedule} disabled={rescheduleMut.isPending}>
              {rescheduleMut.isPending ? 'Saving…' : 'Save new date & time'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
